import { useCallback, useEffect, useState } from "react";
import { api, API, formatErr } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, CreditCard, Landmark, FileText } from "lucide-react";
import { toast } from "sonner";
import { SUB_STATUS_CLS, PAY_STATUS, fmtD, fmtDT, tl } from "@/lib/subscription";

const STATUS_HELP = {
  pending_approval: "İşletmeniz onaylandığında ücretsiz deneme süreniz otomatik başlar.",
  trial: "Deneme süresince tüm hizmet sağlayıcı özelliklerini kullanabilirsiniz.",
  active: "Aboneliğiniz aktif. Yeni paket alırsanız süre mevcut bitişin üzerine eklenir.",
  expired: "Yeni teklif, ilan ve iş alımı kapalı. Devam eden işlerinizi tamamlayabilirsiniz. Aşağıdan paket seçip ödeme bildirin.",
  suspended: "Hesabınız askıya alındı. Ayrıntı için Yol Hattı ile iletişime geçin.",
  exempt: "İşletmeniz süresiz ücretsiz kullanım hakkına sahip.",
  migration_pending: "Mevcut işletmeler için geçiş politikası belirlenene kadar tüm özellikleri kısıtlamasız kullanabilirsiniz.",
};

const StatusCard = ({ s }) => (
  <div className="rounded-2xl border p-4" data-testid="sub-status-card">
    <div className="flex items-center justify-between flex-wrap gap-2">
      <h3 className="font-semibold">Abonelik Durumu</h3>
      <Badge className={SUB_STATUS_CLS[s.status]} data-testid="sub-status">{s.status_label}</Badge>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
      <div><div className="text-xs text-muted-foreground">Deneme başlangıcı</div><div className="font-semibold">{fmtD(s.trial_started_at)}</div></div>
      <div><div className="text-xs text-muted-foreground">Deneme bitişi</div><div className="font-semibold" data-testid="sub-trial-end">{fmtD(s.trial_ends_at)}</div></div>
      <div><div className="text-xs text-muted-foreground">Ücretli abonelik bitişi</div><div className="font-semibold" data-testid="sub-paid-until">{fmtD(s.paid_until)}</div></div>
      <div><div className="text-xs text-muted-foreground">Kalan süre</div><div className="font-semibold" data-testid="sub-days-left">{s.days_left != null ? `${s.days_left} gün` : "—"}</div></div>
    </div>
    <p className="text-sm text-muted-foreground mt-3" data-testid="sub-status-help">{s.trial_review === "pending" ? "Ücretsiz deneme hakkınız yönetici incelemesinde. " : ""}{STATUS_HELP[s.status]}</p>
  </div>
);

const PayForm = ({ pkg, banks, onDone }) => {
  const [f, setF] = useState({ bank_account_id: banks[0]?.id || "", payer_name: "", transfer_date: new Date().toISOString().slice(0, 10), reference: "", note: "", receipt_path: "" });
  const [busy, setBusy] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((s) => ({ ...s, receipt_path: data.path })); toast.success("Dekont yüklendi");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const submit = async () => {
    setBusy(true);
    try { await api.post("/provider/sub-payments", { ...f, package_id: pkg.id }); toast.success("Ödeme bildiriminiz alındı. Yönetici doğruladıktan sonra aboneliğiniz aktifleşir."); onDone(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border-2 border-primary p-4 space-y-3" data-testid="sub-pay-form">
      <div className="font-semibold">Havale / EFT bildirimi · {pkg.name} · {tl(pkg.price_tl)}</div>
      <p className="text-xs text-muted-foreground">Önce aşağıdaki hesaplardan birine <b>{tl(pkg.price_tl)}</b> gönderin, sonra bu formu doldurun. Açıklamaya e-posta adresinizi yazmanız eşleştirmeyi hızlandırır.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="min-w-0"><Label className="text-xs">Gönderilen hesap</Label>
          <Select value={f.bank_account_id} onValueChange={up("bank_account_id")}><SelectTrigger className="mt-1 w-full [&>span]:truncate" data-testid="sub-pay-bank"><SelectValue /></SelectTrigger>
            <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.bank_name} · {b.iban}</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="text-xs">Gönderen adı soyadı / unvan</Label><Input className="mt-1" value={f.payer_name} onChange={up("payer_name")} maxLength={120} data-testid="sub-pay-payer" /></div>
        <div><Label className="text-xs">Havale tarihi</Label><Input type="date" className="mt-1" value={f.transfer_date} onChange={up("transfer_date")} data-testid="sub-pay-date" /></div>
        <div><Label className="text-xs">Dekont / işlem no (ops.)</Label><Input className="mt-1" value={f.reference} onChange={up("reference")} maxLength={120} data-testid="sub-pay-ref" /></div>
      </div>
      <div><Label className="text-xs">Dekont (ops., görsel veya PDF)</Label>
        <input type="file" accept="image/*,application/pdf" className="mt-1 block text-sm" disabled={busy} onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="sub-pay-receipt" />
        {f.receipt_path && <span className="text-xs text-[hsl(var(--success))]">Dekont eklendi</span>}</div>
      <div><Label className="text-xs">Not (ops.)</Label><Textarea rows={2} className="mt-1" value={f.note} onChange={up("note")} maxLength={500} data-testid="sub-pay-note" /></div>
      <p className="text-[11px] text-muted-foreground">Dekont yüklemek aboneliği tek başına aktifleştirmez; ödeme Yol Hattı tarafından banka hesabında doğrulandıktan sonra aktifleşir.</p>
      <Button onClick={submit} disabled={busy || f.payer_name.trim().length < 2 || !f.bank_account_id} className="rounded-full" data-testid="sub-pay-submit">
        {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Ödeme bildirimini gönder</Button>
    </div>
  );
};

const openReceipt = async (pid) => {
  try {
    const r = await fetch(`${API}/sub-payments/${pid}/receipt`, { credentials: "include" });
    if (!r.ok) throw new Error();
    window.open(URL.createObjectURL(await r.blob()), "_blank", "noopener");
  } catch { toast.error("Dekont açılamadı"); }
};

const PaymentList = ({ items, onCancel }) => (
  <div className="rounded-2xl border p-4" data-testid="sub-payments">
    <h3 className="font-semibold mb-2">Abonelik ödemelerim</h3>
    {!items.length && <div className="text-sm text-muted-foreground">Henüz abonelik ödemesi yok.</div>}
    <div className="grid gap-2 text-sm">
      {items.map((p) => (
        <div key={p.id} className="rounded-lg border p-2 flex items-center justify-between flex-wrap gap-2" data-testid={`sub-payment-${p.id}`}>
          <div>
            <div><b>{p.package_name}</b> · {tl(p.amount_tl)} · Havale/EFT</div>
            <div className="text-xs text-muted-foreground">Bildirim: {fmtDT(p.created_at)} · Havale: {fmtD(p.transfer_date)} · {p.bank_snapshot?.bank_name}</div>
            {p.reject_reason && <div className="text-xs text-destructive">Ret nedeni: {p.reject_reason}</div>}
            {p.status === "approved" && <div className="text-xs">Yeni bitiş: {fmtD(p.paid_until_after)}</div>}
          </div>
          <div className="flex items-center gap-2">
            {p.receipt_path && <Button size="sm" variant="ghost" onClick={() => openReceipt(p.id)} data-testid={`sub-payment-receipt-${p.id}`}><FileText className="w-4 h-4" /></Button>}
            {p.status === "pending" && <Button size="sm" variant="outline" onClick={() => onCancel(p.id)} data-testid={`sub-payment-cancel-${p.id}`}>İptal et</Button>}
            <Badge className={PAY_STATUS[p.status][1]} data-testid={`sub-payment-status-${p.id}`}>{PAY_STATUS[p.status][0]}</Badge>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const SubscriptionTab = () => {
  const [d, setD] = useState(null);
  const [pick, setPick] = useState(null);
  const load = useCallback(async () => {
    const g = (u, def) => api.get(u).then((r) => r.data).catch(() => def);
    const [s, pk, banks, pays, legacy] = await Promise.all([g("/provider/subscription", null), g("/provider/sub-packages", []), g("/bank-accounts/active", []), g("/provider/sub-payments", []), g("/provider/payments", [])]);
    setD({ s, pk, banks, pays, legacy }); setPick(null);
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="text-sm text-muted-foreground">Yükleniyor…</div>;
  if (!d.s) return <div className="text-sm text-destructive">Abonelik bilgisi alınamadı.</div>;
  const pending = d.pays.some((p) => p.status === "pending");
  const cancel = async (id) => { if (!window.confirm("Ödeme bildirimi iptal edilsin mi?")) return; try { await api.post(`/provider/sub-payments/${id}/cancel`); toast.success("İptal edildi"); load(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } };
  const canBuy = !["pending_approval", "suspended", "exempt"].includes(d.s.status);
  return (
    <div className="space-y-4" data-testid="subscription-tab">
      <StatusCard s={d.s} />
      {canBuy && (
        <div className="rounded-2xl border p-4" data-testid="sub-packages">
          <h3 className="font-semibold mb-2">Abonelik paketleri</h3>
          {!d.pk.length && <div className="text-sm text-muted-foreground" data-testid="sub-no-packages">Satışta paket bulunmuyor. Fiyatlar Yol Hattı tarafından belirlendiğinde burada görünecek.</div>}
          <div className="grid sm:grid-cols-3 gap-3">
            {d.pk.map((p) => (
              <button key={p.id} type="button" disabled={pending || !d.banks.length} onClick={() => setPick(p)} data-testid={`sub-package-${p.id}`}
                className={`text-left rounded-2xl border-2 p-4 transition-colors disabled:opacity-50 ${pick?.id === p.id ? "border-primary bg-primary/10" : "border-border hover:border-primary/60"}`}>
                <div className="font-bold">{p.name}</div>
                <div className="text-2xl font-extrabold mt-1">{tl(p.price_tl)}</div>
                <div className="text-xs text-muted-foreground">{p.duration_days} gün</div>
                {p.description && <div className="text-xs mt-2">{p.description}</div>}
              </button>
            ))}
          </div>
          {pending && <p className="text-xs text-amber-700 mt-2">Onay bekleyen bir ödeme bildiriminiz var; sonuçlanınca yeni bildirim yapabilirsiniz.</p>}
          {!d.banks.length && d.pk.length > 0 && <p className="text-xs text-muted-foreground mt-2">Yol Hattı banka hesabı henüz tanımlanmadı.</p>}
        </div>
      )}
      {pick && <PayForm pkg={pick} banks={d.banks} onDone={load} />}
      <div className="rounded-2xl border p-4 grid sm:grid-cols-2 gap-3 text-sm">
        <div className="flex gap-2"><Landmark className="w-5 h-5 shrink-0" /><div><b>Havale / EFT</b><div className="text-xs text-muted-foreground">Kullanılabilir. Yönetici doğrulamasıyla aktifleşir.</div></div></div>
        <div className="flex gap-2 opacity-70" data-testid="sub-card-unavailable"><CreditCard className="w-5 h-5 shrink-0" /><div><b>Kredi / banka kartı</b><div className="text-xs text-muted-foreground">Henüz aktif değil. Online ödeme sağlayıcısı bağlandığında açılacak.</div></div></div>
      </div>
      <PaymentList items={d.pays} onCancel={cancel} />
      {d.legacy.length > 0 && (
        <div className="rounded-2xl border p-4 text-sm" data-testid="sub-legacy-payments">
          <h3 className="font-semibold mb-2">Önceki üyelik ödemeleri</h3>
          {d.legacy.map((p) => <div key={p.id} className="flex justify-between border-b last:border-0 py-1"><span>{fmtD(p.payment_date)} · {tl(p.amount)}</span><span>{PAY_STATUS[p.status]?.[0] || p.status}</span></div>)}
        </div>
      )}
    </div>
  );
};
